import { useEffect, useRef, useState } from "react"
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Color from "@tiptap/extension-color"
import Highlight from "@tiptap/extension-highlight"
import Placeholder from "@tiptap/extension-placeholder"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import Underline from "@tiptap/extension-underline"
import TextStyle from "@tiptap/extension-text-style"
import TextAlign from "@tiptap/extension-text-align"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { all, createLowlight } from 'lowlight'
import './Editor.css'


import Document from "./Component/extensions/Document"
import Selection from "./Component/extensions/Selection"
import Heading from "./Component/extensions/Heading"
import TextMenu from "./Component/TextMenu/TextMenu"
import { LinkMenu } from "./Component/LinkMenu"
import { TrailingNode } from "./Component/extensions/TrailingNode"
import HorizontalRule from "./Component/extensions/HorizontalRule"
import SlashCommand from "./Component/SlashCommand/SlashCommand"
import FontSize from "./Component/extensions/FontSize"
import Link from "@tiptap/extension-link"
import Table from "./Component/extensions/Table/Table"
import TableCell from "./Component/extensions/Table/Cell"
import TableHeader from "./Component/extensions/Table/Header"
import TableRow from "./Component/extensions/Table/Row"
import Quote from "./Component/extensions/BlockquoteFigure/Quote"
import QuoteCaption from "./Component/extensions/BlockquoteFigure/QuoteCaption"
import BlockquoteFigure from "./Component/extensions/BlockquoteFigure/BlockquoteFigure"
import Columns from "./Component/extensions/MultiColumn/Columns"
import Column from "./Component/extensions/MultiColumn/Column"
import { TableOfContentsNode } from "./Component/extensions/TableOfContentsNode/TableOfContentsNode"
import { Button } from "./Component/extensions/Button/Button"
import TableRowMenu from "./Component/extensions/Table/menus/TableRow"
import TableColumnMenu from "./Component/extensions/Table/menus/TableColumn"
import ButtonBubbleMenu from "./Component/extensions/Button/ButtonBubbleMenu"
import { ImageEmbeds } from "./Component/extensions/Image/Image"
import { ImageBlock } from "./Component/extensions/Image/ImageBlock"
import { VideoBlock } from "./Component/extensions/Video/VideoBlock"
import { VideoEmbeds } from "./Component/extensions/Video/Video"
import { Category } from "./Component/extensions/Category/Category"
import { CategoryBlock } from "./Component/extensions/Category/CategoryBlock"
import { ParentCategory } from "./Component/extensions/ParentCategory/ParentCategory"
import { ParentCategoryBlock } from "./Component/extensions/ParentCategory/ParentCategoryBlock"
import { Products } from "./Component/extensions/Products/Products"
import TopDealImageBubbleMenu from "./Component/extensions/TopDeal/TopDealImageBubbleMenu"
import ImageBubbleMenu from "./Component/extensions/Image/ImageBubbleMenu"
import VideoBubbleMenu from "./Component/extensions/Video/VideoBubbleMenu"
import TopDeal from "./Component/extensions/TopDeal/TopDeal"
import TopDealItem from "./Component/extensions/TopDeal/TopDealItem"
import ProductsBubbleMenu from "./Component/extensions/Products/ProductsBubbleMenu"
import { ProductsBlockView } from "./Component/extensions/Products/ProductsBlockView"
import { Brand } from "./Component/extensions/Brand/Brand"
import BrandBubbleMenu from "./Component/extensions/Brand/BrandBubbleMenu"
import { WhyChooseMalMart } from "./Component/extensions/WhyChooseMalMart/WhyChooseMalMart"
import { OurServices } from "./Component/extensions/OurServices/OurServices"
import { ReviewBlock } from "./Component/extensions/Review/Review"
import { WhyChooseUs } from "./Component/extensions/WhyChooseUs/WhyChooseUs"
import { Offers } from "./Component/extensions/Offers/Offers"
import { WhyHomeOwnersTrustUs } from "./Component/extensions/WhyHomeOwnersTrustUs/WhyHomeOwnersTrustUs"
const lowlight = createLowlight(all)

export const EditorComponent = ({ setUpdatedContent, editorContent, type = "", id = "" }) => {
    const mainContainerRef = useRef(null)
    const menuRef = useRef();
    const dropdownRef = useRef();
    const editor = useEditor({
        onUpdate: ({ editor }) => {
            let html = editor.getHTML();
            html = html.replace(/<p>(\s|<br>)*<\/p>/g, '').trim();
            setUpdatedContent(html);
        },
        extensions: [
            Document,
            Selection,
            Heading.configure({
                levels: [1, 2, 3, 4, 5, 6],
                HTMLAttributes: {
                    class: 'heading',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Highlight.configure({ multicolor: true }),
            Placeholder.configure({
                includeChildren: true,
                showOnlyCurrent: false,
                placeholder: ({ node }) => {
                    if (node.type.name === 'heading') {
                        return `Heading ${node.attrs.level}`;
                    }

                    return "Type '/' for content";
                }
            }),
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            TrailingNode,
            StarterKit.configure({
                document: false,
                dropcursor: false,
                heading: false,
                horizontalRule: false,
                blockquote: false,
                history: true,
                codeBlock: false,
            }),
            // TableOfContents.configure({
            //     getIndex: getHierarchicalIndexes,
            // }),
            TableOfContentsNode,
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
            }),
            CodeBlockLowlight.configure({
                lowlight,
                defaultLanguage: 'javascript',
                HTMLAttributes: {
                    class: 'code-block',
                }
            }),
            HorizontalRule,
            SlashCommand,
            FontSize,
            Color,
            Subscript,
            Superscript,
            Underline,
            TextStyle,
            Table,
            TableCell,
            TableHeader,
            TableRow,
            Quote,
            QuoteCaption,
            BlockquoteFigure,
            Columns,
            Column,
            Button,
            Category,
            CategoryBlock,
            ParentCategory,
            ParentCategoryBlock,
            Products,
            ProductsBlockView,
            Brand,
            WhyChooseMalMart,
            WhyChooseUs,
            OurServices,
            Offers,
            WhyHomeOwnersTrustUs,
            ReviewBlock,
            TopDealItem,
            TopDeal,
            ImageBlock,
            ImageEmbeds,
            VideoBlock,
            VideoEmbeds,
            File,
        ],
        editorProps: {
            handlePaste: function (view, event) {
                const clipboardData = event.clipboardData;
                const text = clipboardData.getData('text');

                // Simple URL validation
                const urlRegex = /(https?:\/\/[^\s]+)/g;
                const iframeRegex = /<iframe.*?src="(.*?)".*?<\/iframe>/g;
                if (iframeRegex.test(text)) {
                    const { state } = view;
                    const { $from, empty } = state.selection;
                    const currentNode = $from.node();
                    const isCurrentNodeEmpty = currentNode.isTextblock && currentNode.content.size === 0;

                    if (empty && isCurrentNodeEmpty) {
                        event.preventDefault();
                        view.dispatch(
                            view.state.tr.replaceSelectionWith(
                                view.state.schema.nodes.urlPaste.create({ url: text })
                            )
                        );
                        return true;
                    }
                } else {
                    if (urlRegex.test(text)) {
                        const { state } = view;
                        const { $from, empty } = state.selection;
                        const currentNode = $from.node();
                        const isCurrentNodeEmpty = currentNode.isTextblock && currentNode.content.size === 0;

                        if (empty && isCurrentNodeEmpty) {
                            event.preventDefault();

                            // Replace the URL with your custom element if the current node is empty
                            const url = text.match(urlRegex)[0];

                            view.dispatch(
                                view.state.tr.replaceSelectionWith(
                                    view.state.schema.nodes.urlPaste.create({ url: url })
                                )
                            );
                            return true;
                        }
                    }
                }
                return false;
            },
            transformPastedHTML: (html) => {
                return html.replace(/<span.*?>/g, '').replace(/<\/span>/g, '');
            },
        },
        content: editorContent || "",
    })

    const [isOpen, setOpen] = useState(false)
    const [moreOption, setMoreOption] = useState(false);

    useEffect(() => {
        const checkIfClickedOutside = (e) => {
            if (isOpen && setOpen && menuRef?.current && !menuRef?.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", checkIfClickedOutside);

        return () => {
            document.removeEventListener("mousedown", checkIfClickedOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        const checkIfClickedOutside = (e) => {
            if (moreOption && setMoreOption && dropdownRef?.current && !dropdownRef?.current.contains(e.target)) {
                setMoreOption(false);
            }
        };
        document.addEventListener("mousedown", checkIfClickedOutside);

        return () => {
            document.removeEventListener("mousedown", checkIfClickedOutside);
        };
    }, [moreOption]);

    useEffect(() => {
        if (editor && editorContent) {
            if ((id !== "") && (id !== undefined)) {
                editor.commands.setContent(editorContent); // Set content in the editor
            } else {
                editor.commands.setContent('');
            }
        }
    }, [editor, editorContent, id]);

    return (
        <>
            <div className='tiptop-editor' ref={mainContainerRef}>
                <EditorContent editor={editor} />
                {/* <ContentItemMenu editor={editor} /> */}
                <TextMenu editor={editor} />
                <LinkMenu editor={editor} appendTo={mainContainerRef} />
                <TableRowMenu editor={editor} appendTo={mainContainerRef} />
                <TableColumnMenu editor={editor} appendTo={mainContainerRef} />
                <ButtonBubbleMenu editor={editor} appendTo={mainContainerRef} />
                <ImageBubbleMenu editor={editor} appendTo={mainContainerRef} type={type} />
                <VideoBubbleMenu editor={editor} appendTo={mainContainerRef} type={type} />
                <TopDealImageBubbleMenu editor={editor} appendTo={mainContainerRef}/>
                <ProductsBubbleMenu editor={editor} appendTo={mainContainerRef}/>
                <BrandBubbleMenu editor={editor} appendTo={mainContainerRef}/>
            </div>
        </>
    )
}